import { CarouselCtaCover } from '@bridebook/web/app-basic/components/supplier/sections/carousel/carousel-cta-cover';
import { IGalleryConfig } from '@bridebook/web/app-basic/components/supplier/sections/gallery/gallery';
import { CSSProperties, DragEventHandler, memo, useCallback, useMemo } from 'react';
import SignupLink from '@bridebook/web/app-shared/components/signup-link/signup-link';
import { TLoggedOutPageClickedSection } from '@bridebook/web/app-shared/lib/types';
import componentStyles from './carousel-image.style';
import { buildImageSrc, OPTIMIZED_IMAGE_QUALITY } from '@bridebook/toolbox/src/buildImageUrl';

const srcDimensionSet = [1600, 1024, 820, 480];

const DEFAULT_HEIGHT = 682;
const DEFAULT_WIDTH = 1024;

export interface CarouselImageProps {
  path: string;
  lazyLoading: boolean;
  highPriority: boolean;
  imgixParams?: Record<string, string | number>;
  showCTALayer?: boolean;
  labelConfig?: {
    header: string;
    desc: string;
    button: string;
    size?: IGalleryConfig['ctaSize'];
  };
  enhanceImg?: boolean;
  clickedSection?: TLoggedOutPageClickedSection;
  imageStyle?: CSSProperties;
}

const CarouselImageComponent = ({
  path,
  lazyLoading,
  highPriority,
  imgixParams = {},
  showCTALayer = false,
  labelConfig = { header: '', desc: '', button: '', size: 'large' },
  enhanceImg,
  clickedSection,
}: CarouselImageProps) => {
  const src = useMemo(
    () =>
      buildImageSrc(path, {
        gravity: 'face',
        fit: 'cover',
        ...imgixParams,
        w: DEFAULT_WIDTH,
        h: DEFAULT_HEIGHT,
        quality: OPTIMIZED_IMAGE_QUALITY,
      }),
    [path, imgixParams, enhanceImg],
  );

  const handleDragStart = useCallback<DragEventHandler<HTMLImageElement>>((e) => {
    e.preventDefault();
  }, []);

  const styles = componentStyles();

  return (
    <SignupLink
      clickedLocation="supplierProfile"
      clickedSection={clickedSection ? clickedSection : 'supplierMediaGallery'}
      cta="supplierProfileImage">
      <img
        // fallback to src with highest value set for browser that does not support srcSet
        src={src}
        srcSet={srcDimensionSet.map((width) => `${src} ${width}w`).join(',')}
        // @ts-expect-error React does not recognize fetchpriority property
        fetchpriority={highPriority ? 'high' : 'low'}
        loading={lazyLoading ? 'lazy' : 'eager'}
        width={DEFAULT_WIDTH}
        height={DEFAULT_HEIGHT}
        onDragStart={handleDragStart}
        sizes={srcDimensionSet
          .map((value, index, arr) => {
            const nextSize = arr[index + 1];
            return nextSize ? `(min-width: ${nextSize}px) ${value}px` : `${value}px`;
          })
          .join(', ')}
        style={styles.image}
      />
      {showCTALayer && (
        <CarouselCtaCover
          header={labelConfig.header}
          desc={labelConfig.desc}
          button={labelConfig.button}
          size={labelConfig?.size}
        />
      )}
    </SignupLink>
  );
};

export const CarouselImage = memo(CarouselImageComponent);
